// Part accumulator + concurrent upload queue.
//
// MediaRecorder timeslice blobs are sequential slices of ONE continuous
// container stream, so the R2 object is just their byte-concatenation. We
// accumulate chunk bytes and cut parts at an EXACT fixed boundary (R2
// requires all non-final parts to be the same size), carrying the remainder.
// Parts upload concurrently (max 2 in flight) WHILE recording continues;
// on slow uplinks data rests in the IndexedDB journal, not RAM.

import type { Journal } from './journal';

export const DEFAULT_PART_SIZE = 10 * 1024 * 1024; // 10 MiB
export const MIN_PART_SIZE = 5 * 1024 * 1024; // R2 minimum for non-final parts

export interface UploaderStats {
  partsUploaded: number;
  partsInFlight: number;
  partsQueued: number;
  bytesUploaded: number;
  bytesBuffered: number;
  totalBytes: number;
}

interface PendingPart {
  partNumber: number;
  start: number; // byte range within the stream
  end: number;
  bytes: Uint8Array;
}

export class PartUploader {
  private pending: Uint8Array[] = [];
  private pendingBytes = 0;
  private cutOffset = 0; // bytes already sliced into parts
  private nextPartNumber = 1;
  private queue: PendingPart[] = [];
  private inFlight = new Set<number>();
  private etags = new Map<number, string>();
  private partRanges = new Map<number, { start: number; end: number }>();
  private ackedBytes = 0;
  private uploadedBytes = 0;
  private totalBytes = 0;
  private seq = 0;
  private failed: Error | null = null;
  private draining: Promise<void> | null = null;

  onStats: (s: UploaderStats) => void = () => {};

  constructor(
    private slug: string,
    private journal: Journal,
    private partSize: number = DEFAULT_PART_SIZE,
    private maxInFlight = 2,
  ) {
    if (partSize < MIN_PART_SIZE) {
      console.warn(
        `[clip] part size ${partSize} below R2 minimum ${MIN_PART_SIZE}; ` +
          'fine against local miniflare, would fail on real R2',
      );
    }
  }

  get stats(): UploaderStats {
    return {
      partsUploaded: this.etags.size,
      partsInFlight: this.inFlight.size,
      partsQueued: this.queue.length,
      bytesUploaded: this.uploadedBytes,
      bytesBuffered: this.pendingBytes,
      totalBytes: this.totalBytes,
    };
  }

  /** Feed one MediaRecorder chunk. Journals it, then cuts full parts. */
  async addChunk(blob: Blob): Promise<void> {
    if (this.failed) throw this.failed;
    const buf = await blob.arrayBuffer();
    const bytes = new Uint8Array(buf);
    const offset = this.totalBytes;
    this.totalBytes += bytes.byteLength;
    const seq = this.seq++;
    // Durability first: journal before buffering for upload.
    await this.journal.putChunk({ slug: this.slug, seq, offset, size: bytes.byteLength, bytes: buf });
    this.pending.push(bytes);
    this.pendingBytes += bytes.byteLength;
    this.cutFullParts();
    this.pump();
    this.onStats(this.stats);
  }

  /** Cut as many exact-partSize parts as the buffer allows. */
  private cutFullParts(): void {
    while (this.pendingBytes >= this.partSize) {
      this.enqueuePart(this.takeBytes(this.partSize));
    }
  }

  private takeBytes(n: number): Uint8Array {
    const out = new Uint8Array(n);
    let filled = 0;
    while (filled < n) {
      const head = this.pending[0];
      const need = n - filled;
      if (head.byteLength <= need) {
        out.set(head, filled);
        filled += head.byteLength;
        this.pending.shift();
      } else {
        out.set(head.subarray(0, need), filled);
        this.pending[0] = head.subarray(need);
        filled += need;
      }
    }
    this.pendingBytes -= n;
    return out;
  }

  private enqueuePart(bytes: Uint8Array): void {
    const partNumber = this.nextPartNumber++;
    const start = this.cutOffset;
    const end = start + bytes.byteLength;
    this.cutOffset = end;
    this.partRanges.set(partNumber, { start, end });
    this.queue.push({ partNumber, start, end, bytes });
  }

  private pump(): void {
    while (this.inFlight.size < this.maxInFlight && this.queue.length > 0 && !this.failed) {
      const part = this.queue.shift()!;
      this.inFlight.add(part.partNumber);
      void this.uploadWithRetry(part)
        .then(async (etag) => {
          this.inFlight.delete(part.partNumber);
          this.etags.set(part.partNumber, etag);
          this.uploadedBytes += part.bytes.byteLength;
          await this.pruneJournal();
          this.onStats(this.stats);
          this.pump();
        })
        .catch((err) => {
          this.inFlight.delete(part.partNumber);
          this.failed = err instanceof Error ? err : new Error(String(err));
          console.error('[clip] part upload permanently failed', err);
          this.onStats(this.stats);
        });
    }
  }

  /** Prune journal rows fully covered by the contiguous acked prefix. */
  private async pruneJournal(): Promise<void> {
    let prefixEnd = 0;
    for (let n = 1; n < this.nextPartNumber; n++) {
      if (!this.etags.has(n)) break;
      prefixEnd = this.partRanges.get(n)!.end;
    }
    if (prefixEnd > this.ackedBytes) {
      this.ackedBytes = prefixEnd;
      await this.journal.pruneThrough(this.slug, prefixEnd);
    }
  }

  private async uploadWithRetry(part: PendingPart, maxAttempts = 5): Promise<string> {
    let lastErr: unknown;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const res = await fetch(`/api/recordings/${this.slug}/parts/${part.partNumber}`, {
          method: 'PUT',
          body: part.bytes as unknown as BodyInit,
        });
        if (!res.ok) throw new Error(`part ${part.partNumber}: HTTP ${res.status}`);
        const body = (await res.json()) as { etag: string };
        return body.etag;
      } catch (err) {
        lastErr = err;
        const delay = Math.min(500 * 2 ** attempt, 8000);
        console.warn(`[clip] part ${part.partNumber} attempt ${attempt + 1} failed, retrying in ${delay}ms`, err);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
  }

  private async waitForQueue(): Promise<void> {
    while ((this.queue.length > 0 || this.inFlight.size > 0) && !this.failed) {
      await new Promise((r) => setTimeout(r, 100));
    }
    if (this.failed) throw this.failed;
  }

  /** Stop: flush the final (short) part, wait for all uploads, complete.
   * `thumbnailDataUri` (SC-115) is the single static JPEG frame app.ts
   * captured from the recording canvas right before calling session.stop()
   * -- piggybacked onto this one already-mandatory request rather than a
   * dedicated endpoint, so storing a library thumbnail costs zero extra
   * round trips. */
  async finish(durationS: number, thumbnailDataUri?: string): Promise<{ status: string }> {
    if (this.draining) throw new Error('finish() already called');
    this.draining = (async () => {})();

    // Final part: whatever remains (< partSize). R2 allows any size for the
    // last part. A zero-byte recording has nothing to flush.
    if (this.pendingBytes > 0) {
      this.enqueuePart(this.takeBytes(this.pendingBytes));
    }
    this.pump();
    await this.waitForQueue();

    const parts = [...this.etags.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([partNumber, etag]) => ({ partNumber, etag }));
    if (parts.length === 0) throw new Error('nothing recorded');

    const res = await fetch(`/api/recordings/${this.slug}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parts, durationS, thumbnailDataUri }),
    });
    if (!res.ok) throw new Error(`complete failed: HTTP ${res.status} ${await res.text()}`);
    const body = (await res.json()) as { status: string };
    await this.journal.finishSession(this.slug);
    this.onStats(this.stats);
    return body;
  }
}
